import { apiClient } from "./apiClient";
import { formatShortDateTime, parseApiDateTime } from "@/utils/date-helpers";
import type { ContactNoteEntry } from "@/types/contact";
import type { ContactSummary, PropertyDetail } from "@/types/entities";

/** UTC ISO timestamp for note created/modified fields. */
function noteTimestampNow(): string {
  return new Date().toISOString();
}

export interface NoteContactRef {
  id: string;
  externalId: string | null;
}

export interface NotePropertyRef {
  id: string;
  externalId: string | null;
}

/** POST/PATCH /api/ui/v1/notes — contact file note body. */
export interface NoteCreatePayload {
  externalId?: string | null;
  sourceCrm: string;
  legalEntityId: string;
  branchId?: string[] | null;
  header: string | null;
  title: string;
  notes: string;
  notesType: "FileNote";
  created: string | null;
  modified: string | null;
  externalAuthorId: string | null;
  externalAuthorName: string | null;
  properties: unknown;
  contacts: NoteContactRef[] | null;
  users: unknown;
  metadata: unknown;
}

/** Nullable note fields preserved on update (from existing {@link ContactNoteEntry}). */
export interface NoteFieldPreserve {
  externalId: string | null;
  sourceCrm: string;
  legalEntityId: string;
  branchId?: unknown;
  header: string | null;
  notesType: string;
  created: string | null;
  modified: string | null;
  externalAuthorId: string | null;
  externalAuthorName: string | null;
  properties: unknown;
  contacts: unknown;
  users: unknown;
  metadata: unknown;
}

export interface BuildContactFileNotePayloadInput {
  sourceCrm: string;
  legalEntityId: string;
  title: string;
  notes: string;
  contactId: string;
  contactExternalId: string | null;
  branchId?: string | null;
  externalAuthorId?: string | null;
  externalAuthorName?: string | null;
  preserve?: NoteFieldPreserve;
}

export interface BuildPropertyFileNotePayloadInput {
  sourceCrm: string;
  legalEntityId: string;
  title: string;
  notes: string;
  propertyId: string;
  propertyExternalId: string | null;
  contacts?: NoteContactRef[] | null;
  branchId?: string | null;
  externalAuthorId?: string | null;
  externalAuthorName?: string | null;
  preserve?: NoteFieldPreserve;
}

/** Map property owners to note `contacts` refs using `GET /contacts` list rows. */
export function resolvePropertyOwnerContactRefs(
  property: PropertyDetail,
  contacts: ContactSummary[],
): NoteContactRef[] {
  const owners = property.owners ?? [];
  if (!owners.length) return [];

  const refs: NoteContactRef[] = [];
  const seen = new Set<string>();

  for (const owner of owners) {
    const contactId = owner.contactId?.trim() ?? "";
    const externalContactId = owner.externalContactId?.trim() ?? "";

    const matched =
      (contactId ? contacts.find((c) => c.id?.trim() === contactId) : undefined) ??
      (externalContactId
        ? contacts.find((c) => c.externalId?.trim() === externalContactId)
        : undefined);

    const id = matched?.id?.trim() || contactId;
    if (!id || seen.has(id)) continue;
    seen.add(id);

    refs.push({
      id,
      externalId: matched?.externalId?.trim() || externalContactId || null,
    });
  }

  return refs;
}

export function noteBranchIds(branchId: string | null | undefined): string[] | null {
  const id = branchId?.trim();
  return id ? [id] : null;
}

/** Prefer selected branch; else first branch on the contact/property record. */
export function resolveNoteBranchIds(
  selectedBranchId: string | null | undefined,
  entityBranchId: unknown,
): string[] | null {
  const fromSettings = noteBranchIds(selectedBranchId);
  if (fromSettings) return fromSettings;
  if (Array.isArray(entityBranchId)) {
    const first = entityBranchId.find((x) => typeof x === "string" && x.trim());
    return first ? [first.trim()] : null;
  }
  if (typeof entityBranchId === "string") {
    return noteBranchIds(entityBranchId);
  }
  return null;
}

/** Wall-clock timestamp for sorting (prefers `created`, then `modified`). */
export function getContactNoteTimestamp(note: ContactNoteEntry): number {
  const raw = note.created?.trim() || note.modified?.trim() || "";
  if (!raw) return 0;
  const parsed = parseApiDateTime(raw) ?? new Date(raw);
  const t = parsed.getTime();
  return Number.isNaN(t) ? 0 : t;
}

/** Newest-first by default; pass `asc` for oldest-first.
 *  Undated notes fall back to API array order (later index = newer). */
export function sortContactNotes(
  notes: ContactNoteEntry[] | null | undefined,
  order: "asc" | "desc" = "desc",
): ContactNoteEntry[] {
  if (!notes?.length) return [];
  const sign = order === "desc" ? 1 : -1;
  return notes
    .map((note, index) => ({ note, index }))
    .sort((a, b) => {
      const ta = getContactNoteTimestamp(a.note);
      const tb = getContactNoteTimestamp(b.note);
      if (ta !== 0 || tb !== 0) {
        const aTime = ta || (order === "desc" ? Number.MAX_SAFE_INTEGER : 0);
        const bTime = tb || (order === "desc" ? Number.MAX_SAFE_INTEGER : 0);
        const byTime = sign * (bTime - aTime);
        if (byTime !== 0) return byTime;
      }
      // Same or missing timestamps: later in the API list = newer
      return sign * (b.index - a.index);
    })
    .map(({ note }) => note);
}

export function isContactFileNote(note: ContactNoteEntry): boolean {
  return (note.notesType ?? "").trim().toLowerCase() === "filenote";
}

export function filterContactFileNotes(notes: ContactNoteEntry[] | null | undefined): ContactNoteEntry[] {
  return (notes ?? []).filter(isContactFileNote);
}

export function formatContactNoteDateTime(value: string | null | undefined): string {
  return formatShortDateTime(value) || (value?.trim() ?? "");
}

export function noteEntryToPreserve(entry: ContactNoteEntry): NoteFieldPreserve {
  return {
    externalId: entry.externalId?.trim() || null,
    sourceCrm: entry.sourceCrm?.trim() ?? "",
    legalEntityId: entry.legalEntityId?.trim() ?? "",
    branchId: entry.branchId ?? null,
    header: entry.header ?? null,
    notesType: entry.notesType ?? "FileNote",
    created: entry.created ?? null,
    modified: entry.modified ?? null,
    externalAuthorId: entry.externalAuthorId ?? null,
    externalAuthorName: entry.externalAuthorName ?? null,
    properties: entry.properties ?? null,
    contacts: entry.contacts ?? null,
    users: entry.users ?? null,
    metadata: entry.metadata ?? null,
  };
}

/** Builds POST/PATCH /notes payload for a contact file note. */
export function buildContactFileNotePayload(
  input: BuildContactFileNotePayloadInput,
): NoteCreatePayload {
  const preserve = input.preserve;
  return {
    ...(preserve?.externalId ? { externalId: preserve.externalId } : {}),
    sourceCrm: preserve?.sourceCrm || input.sourceCrm,
    legalEntityId: preserve?.legalEntityId || input.legalEntityId,
    branchId:
      noteBranchIds(input.branchId) ??
      (Array.isArray(preserve?.branchId)
        ? (preserve.branchId as string[])
        : noteBranchIds(typeof preserve?.branchId === "string" ? preserve.branchId : null)),
    header: preserve?.header ?? null,
    title: input.title,
    notes: input.notes,
    notesType: "FileNote",
    created: preserve?.created ?? noteTimestampNow(),
    modified: noteTimestampNow(),
    externalAuthorId: input.externalAuthorId ?? preserve?.externalAuthorId ?? null,
    externalAuthorName: input.externalAuthorName ?? preserve?.externalAuthorName ?? null,
    properties: preserve?.properties ?? null,
    contacts: [
      {
        id: input.contactId,
        externalId: input.contactExternalId,
      },
    ],
    users: preserve?.users ?? null,
    metadata: preserve?.metadata ?? null,
  };
}

/** Builds POST/PATCH /notes payload for a property file note. */
export function buildPropertyFileNotePayload(
  input: BuildPropertyFileNotePayloadInput,
): NoteCreatePayload {
  const preserve = input.preserve;
  return {
    ...(preserve?.externalId ? { externalId: preserve.externalId } : {}),
    sourceCrm: preserve?.sourceCrm || input.sourceCrm,
    legalEntityId: preserve?.legalEntityId || input.legalEntityId,
    branchId:
      noteBranchIds(input.branchId) ??
      (Array.isArray(preserve?.branchId)
        ? (preserve.branchId as string[])
        : noteBranchIds(typeof preserve?.branchId === "string" ? preserve.branchId : null)),
    header: preserve?.header ?? null,
    title: input.title,
    notes: input.notes,
    notesType: "FileNote",
    created: preserve?.created ?? noteTimestampNow(),
    modified: noteTimestampNow(),
    externalAuthorId: input.externalAuthorId ?? preserve?.externalAuthorId ?? null,
    externalAuthorName: input.externalAuthorName ?? preserve?.externalAuthorName ?? null,
    properties: [
      {
        id: input.propertyId,
        externalId: input.propertyExternalId,
      },
    ],
    contacts:
      input.contacts ??
      (preserve?.contacts as NoteContactRef[] | null) ??
      null,
    users: preserve?.users ?? null,
    metadata: preserve?.metadata ?? null,
  };
}

export const notesService = {
  listForProperty(propertyId: string): Promise<ContactNoteEntry[]> {
    return apiClient
      .get<ContactNoteEntry[]>(`/notes/property/${propertyId}`)
      .then((r) => filterContactFileNotes(r.data));
  },

  create(body: NoteCreatePayload): Promise<ContactNoteEntry> {
    console.log("Notes payload", body);
    return apiClient.post<ContactNoteEntry>("/notes", body).then((r) => r.data);
  },

  update(id: string, body: NoteCreatePayload): Promise<ContactNoteEntry> {
    console.log("Update payload", body);
    return apiClient.patch<ContactNoteEntry>(`/notes/${id}`, body).then((r) => r.data);
  },
};
